import { useEffect, useState, useCallback } from "react";
import Layout from "@/components/Layout";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Plus, Trash2, Check, Pencil, KeyRound, Pause, Play } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const blankForm = { email: "", password: "", name: "", role: "location_user", location_id: "", managed_location_ids: [] };

export default function UsersPage() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blankForm);
  const [saving, setSaving] = useState(false);

  // Edit dialog
  const [editing, setEditing] = useState(null); // user being edited
  const [editForm, setEditForm] = useState({ name: "", email: "", role: "location_user", location_id: "", managed_location_ids: [] });
  const [editSaving, setEditSaving] = useState(false);

  // Reset password dialog
  const [resettingUser, setResettingUser] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [resetSaving, setResetSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [u, l] = await Promise.all([api.get("/users"), api.get("/locations")]);
      setUsers(u.data);
      setLocations(l.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const create = async () => {
    if (!form.email || !form.password || !form.name)
      return toast.error("All fields are required");
    if (form.role === "location_user" && !form.location_id)
      return toast.error("Assign a location for location users");
    if (form.role === "manager" && form.managed_location_ids.length === 0)
      return toast.error("Pick at least one location for the manager");
    setSaving(true);
    try {
      await api.post("/users", {
        email: form.email.trim(),
        password: form.password,
        name: form.name.trim(),
        role: form.role,
        location_id: form.role === "location_user" ? form.location_id : null,
        managed_location_ids: form.role === "manager" ? form.managed_location_ids : [],
      });
      toast.success("User created");
      setForm(blankForm);
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to create");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!confirm("Remove this user?")) return;
    try {
      await api.delete(`/users/${id}`);
      toast.success("User removed");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
    }
  };

  const approve = async (id) => {
    try {
      await api.patch(`/users/${id}/approve`);
      toast.success("User approved — they can now sign in");
      load();
    } catch (e) {
      toast.error(formatApiError(e) || "Approve failed");
    }
  };

  const openEdit = (u) => {
    setEditing(u);
    setEditForm({
      name: u.name || "",
      email: u.email || "",
      role: u.role,
      location_id: u.location_id || "",
      managed_location_ids: u.managed_location_ids || [],
    });
  };

  const saveEdit = async () => {
    if (!editing) return;
    if (!editForm.name.trim() || !editForm.email.trim()) {
      return toast.error("Name and email are required");
    }
    if (editForm.role === "location_user" && !editForm.location_id) {
      return toast.error("Assign a location for location users");
    }
    if (editForm.role === "manager" && editForm.managed_location_ids.length === 0) {
      return toast.error("Pick at least one location for the manager");
    }
    setEditSaving(true);
    try {
      await api.patch(`/users/${editing.id}`, {
        name: editForm.name.trim(),
        email: editForm.email.trim(),
        role: editForm.role,
        location_id: editForm.role === "location_user" ? editForm.location_id : null,
        managed_location_ids: editForm.role === "manager" ? editForm.managed_location_ids : [],
      });
      toast.success("User updated");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Update failed");
    } finally {
      setEditSaving(false);
    }
  };

  const toggleManagedLocation = (formSetter, currentForm, locId) => {
    const has = currentForm.managed_location_ids.includes(locId);
    formSetter({
      ...currentForm,
      managed_location_ids: has
        ? currentForm.managed_location_ids.filter((x) => x !== locId)
        : [...currentForm.managed_location_ids, locId],
    });
  };

  const toggleSuspend = async (u) => {
    const action = u.status === "suspended" ? "active" : "suspended";
    const verb = action === "suspended" ? "Suspend" : "Reactivate";
    if (!confirm(`${verb} ${u.name}?`)) return;
    try {
      await api.patch(`/users/${u.id}`, { status: action });
      toast.success(action === "suspended" ? "User suspended" : "User reactivated");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Status change failed");
    }
  };

  const resetPassword = async () => {
    if (!resettingUser) return;
    if ((newPassword || "").length < 6) {
      return toast.error("Password must be at least 6 characters");
    }
    setResetSaving(true);
    try {
      await api.post(`/users/${resettingUser.id}/password`, { password: newPassword });
      toast.success(`Password reset for ${resettingUser.name}`);
      setResettingUser(null);
      setNewPassword("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Reset failed");
    } finally {
      setResetSaving(false);
    }
  };

  const locationName = (id) => locations.find((l) => l.id === id)?.name || "—";

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-8 lg:py-10">
        <div className="flex items-end justify-between gap-4 mb-8 flex-wrap">
          <div>
            <p className="text-xs tracking-[0.2em] uppercase text-gray-500 mb-2">Admin</p>
            <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">Users</h1>
            <p className="text-sm text-gray-600 mt-2">
              Create accounts for each location's team and assign them to a site.
            </p>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#0055FF] hover:bg-[#0044CC] h-11" data-testid="add-user-btn">
                <Plus size={16} className="mr-1" /> New user
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="add-user-dialog">
              <DialogHeader>
                <DialogTitle className="font-display tracking-tight text-2xl">
                  New user
                </DialogTitle>
                <DialogDescription>
                  Create a login for a teammate and assign them to a location.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-[0.2em] text-gray-700">Name</Label>
                    <Input
                      data-testid="user-name"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-[0.2em] text-gray-700">Email</Label>
                    <Input
                      type="email"
                      data-testid="user-email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-[0.2em] text-gray-700">Password</Label>
                    <Input
                      type="password"
                      data-testid="user-password"
                      value={form.password}
                      onChange={(e) => setForm({ ...form, password: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-[0.2em] text-gray-700">Role</Label>
                    <Select
                      value={form.role}
                      onValueChange={(v) => setForm({
                        ...form,
                        role: v,
                        location_id: v === "location_user" ? form.location_id : "",
                        managed_location_ids: v === "manager" ? form.managed_location_ids : [],
                      })}
                    >
                      <SelectTrigger data-testid="user-role">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="location_user">Location user</SelectItem>
                        <SelectItem value="manager">Manager</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {form.role === "location_user" && (
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-[0.2em] text-gray-700">Location</Label>
                    <Select
                      value={form.location_id}
                      onValueChange={(v) => setForm({ ...form, location_id: v })}
                    >
                      <SelectTrigger data-testid="user-location">
                        <SelectValue placeholder="Choose a location" />
                      </SelectTrigger>
                      <SelectContent>
                        {locations.map((l) => (
                          <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {locations.length === 0 && (
                      <p className="text-xs text-red-600">Add a location first.</p>
                    )}
                  </div>
                )}

                {form.role === "manager" && (
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-[0.2em] text-gray-700">Managed locations</Label>
                    <div className="flex flex-wrap gap-2" data-testid="user-managed-locations">
                      {locations.map((l) => {
                        const active = form.managed_location_ids.includes(l.id);
                        return (
                          <button
                            type="button"
                            key={l.id}
                            data-testid={`user-managed-loc-${l.id}`}
                            onClick={() => toggleManagedLocation(setForm, form, l.id)}
                            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                              active
                                ? "bg-[#0055FF] text-white border-[#0055FF]"
                                : "bg-white text-gray-700 border-gray-300 hover:border-[#0055FF]"
                            }`}
                          >
                            {l.name}
                          </button>
                        );
                      })}
                    </div>
                    <p className="text-xs text-gray-500">Manager can edit schedules & employees for these stores only.</p>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button
                  data-testid="save-user"
                  className="bg-[#0055FF] hover:bg-[#0044CC]"
                  onClick={create}
                  disabled={saving}
                >
                  Create
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="users-table">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Location</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((u) => {
                const pending = u.status === "pending";
                const suspended = u.status === "suspended";
                const isSelf = u.id === currentUser?.id;
                return (
                  <TableRow
                    key={u.id}
                    data-testid={`user-row-${u.id}`}
                    className={pending ? "bg-amber-50/50" : suspended ? "bg-gray-50 opacity-70" : ""}
                  >
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2 flex-wrap">
                        {u.name}
                        {pending && (
                          <span className="pill" style={{ background: "#FFFBEB", color: "#D97706", border: "1px solid #FCD34D" }} data-testid={`user-pending-${u.id}`}>
                            Pending approval
                          </span>
                        )}
                        {suspended && (
                          <span className="pill" style={{ background: "#F3F4F6", color: "#4B5563", border: "1px solid #D1D5DB" }} data-testid={`user-suspended-${u.id}`}>
                            Suspended
                          </span>
                        )}
                        {isSelf && (
                          <span className="pill" style={{ background: "#EFF6FF", color: "#1D4ED8", border: "1px solid #BFDBFE" }}>
                            You
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-gray-600">{u.email}</TableCell>
                    <TableCell>
                      <span className={`pill ${u.role === "admin" ? "prio-high" : u.role === "manager" ? "prio-medium" : "prio-low"}`}>
                        {u.role === "admin" ? "Admin" : u.role === "manager" ? "Manager" : "Location"}
                      </span>
                    </TableCell>
                    <TableCell className="text-gray-600">
                      {u.role === "admin"
                        ? "—"
                        : u.role === "manager"
                          ? ((u.managed_location_ids || []).map(locationName).join(", ") || "—")
                          : locationName(u.location_id)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-1 flex-wrap">
                        {pending && (
                          <Button
                            size="sm"
                            data-testid={`approve-user-${u.id}`}
                            onClick={() => approve(u.id)}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white h-8"
                          >
                            <Check size={14} className="mr-1" /> Approve
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openEdit(u)}
                          data-testid={`edit-user-${u.id}`}
                          className="text-gray-500 hover:text-[#0055FF] h-8 w-8"
                          aria-label="Edit"
                          title="Edit user"
                        >
                          <Pencil size={15} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => { setResettingUser(u); setNewPassword(""); }}
                          data-testid={`reset-password-${u.id}`}
                          className="text-gray-500 hover:text-amber-600 h-8 w-8"
                          aria-label="Reset password"
                          title="Reset password"
                        >
                          <KeyRound size={15} />
                        </Button>
                        {!isSelf && !pending && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => toggleSuspend(u)}
                            data-testid={`toggle-suspend-${u.id}`}
                            className={`h-8 w-8 ${suspended ? "text-emerald-600 hover:text-emerald-700" : "text-gray-500 hover:text-amber-600"}`}
                            aria-label={suspended ? "Reactivate" : "Suspend"}
                            title={suspended ? "Reactivate user" : "Suspend user"}
                          >
                            {suspended ? <Play size={15} /> : <Pause size={15} />}
                          </Button>
                        )}
                        {!isSelf && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => remove(u.id)}
                            data-testid={`delete-user-${u.id}`}
                            className="text-gray-400 hover:text-red-600 h-8 w-8"
                            aria-label={pending ? "Reject" : "Delete"}
                            title={pending ? "Reject" : "Delete"}
                          >
                            <Trash2 size={15} />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
              {users.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-sm text-gray-500 py-8">
                    No users yet.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Edit user dialog */}
      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent data-testid="edit-user-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight text-2xl">Edit user</DialogTitle>
            <DialogDescription>
              Update name, email, role and assigned location.
              {editing?.id === currentUser?.id && " You can't change your own role."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.2em] text-gray-700">Name</Label>
                <Input
                  data-testid="edit-user-name"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.2em] text-gray-700">Email</Label>
                <Input
                  type="email"
                  data-testid="edit-user-email"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.2em] text-gray-700">Role</Label>
              <Select
                value={editForm.role}
                onValueChange={(v) => setEditForm({
                  ...editForm,
                  role: v,
                  location_id: v === "location_user" ? editForm.location_id : "",
                  managed_location_ids: v === "manager" ? editForm.managed_location_ids : [],
                })}
                disabled={editing?.id === currentUser?.id}
              >
                <SelectTrigger data-testid="edit-user-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="location_user">Location user</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {editForm.role === "location_user" && (
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.2em] text-gray-700">Location</Label>
                <Select
                  value={editForm.location_id}
                  onValueChange={(v) => setEditForm({ ...editForm, location_id: v })}
                >
                  <SelectTrigger data-testid="edit-user-location">
                    <SelectValue placeholder="Choose a location" />
                  </SelectTrigger>
                  <SelectContent>
                    {locations.map((l) => (
                      <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {editForm.role === "manager" && (
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.2em] text-gray-700">Managed locations</Label>
                <div className="flex flex-wrap gap-2" data-testid="edit-user-managed-locations">
                  {locations.map((l) => {
                    const active = editForm.managed_location_ids.includes(l.id);
                    return (
                      <button
                        type="button"
                        key={l.id}
                        data-testid={`edit-user-managed-loc-${l.id}`}
                        onClick={() => toggleManagedLocation(setEditForm, editForm, l.id)}
                        className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                          active
                            ? "bg-[#0055FF] text-white border-[#0055FF]"
                            : "bg-white text-gray-700 border-gray-300 hover:border-[#0055FF]"
                        }`}
                      >
                        {l.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button
              data-testid="save-edit-user"
              className="bg-[#0055FF] hover:bg-[#0044CC]"
              onClick={saveEdit}
              disabled={editSaving}
            >
              Save changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset password dialog */}
      <Dialog open={!!resettingUser} onOpenChange={(v) => !v && setResettingUser(null)}>
        <DialogContent data-testid="reset-password-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight text-2xl">Reset password</DialogTitle>
            <DialogDescription>
              Set a new password for <strong>{resettingUser?.name}</strong>. Share it with them
              securely — they can sign in with it immediately.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.2em] text-gray-700">New password</Label>
            <Input
              type="text"
              data-testid="new-password-input"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="At least 6 characters"
            />
            <p className="text-xs text-gray-500">
              Tip: use something short and memorable so you can text it to your teammate.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResettingUser(null)}>Cancel</Button>
            <Button
              data-testid="confirm-reset-password"
              className="bg-amber-600 hover:bg-amber-700 text-white"
              onClick={resetPassword}
              disabled={resetSaving || newPassword.length < 6}
            >
              Reset password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
